import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Table, Button, Select, Tag, Row, Col, Typography } from "antd";
import { IdcardOutlined } from "@ant-design/icons";
import axios from "axios";
import Cookies from "js-cookie";
import { firebaseAPI } from "../firebaseConfig";
import axiosInstance from "../utils/axiosInstance";

const { Option } = Select;
const { Title } = Typography;

const JudgeHome = () => {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRound, setFilterRound] = useState("");
  const [filterProgram, setFilterProgram] = useState("");
  const [filterEvaluated, setFilterEvaluated] = useState("");
  const [filterDisability, setFilterDisability] = useState("");
  const location = useLocation();

  const [program] = useState(decodeURI(location.pathname.split("/")[1]));

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const token = Cookies.get("token") || "";
        const response = await axiosInstance.get(
          `${firebaseAPI}/submissions/judge`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSubmissions(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching judge submissions:", error);
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, []);

  useEffect(() => {
    const filtered = submissions.filter((submission) => {
      const form = submission?.form;
      const profile = submission?.participantProfile;
      const judgeScore = submission?.judgeAssignment?.judgeScore;

      if (filterRound && form?.name !== filterRound) return false;
      if (filterProgram && form?.program !== filterProgram) return false;

      if (
        filterEvaluated === "evaluated" &&
        (judgeScore === null || typeof judgeScore === "undefined")
      )
        return false;
      if (
        filterEvaluated === "Not-evaluated" &&
        judgeScore !== null &&
        typeof judgeScore !== "undefined"
      )
        return false;

      if (filterDisability === "with" && !profile?.hasDisability) return false;
      if (filterDisability === "without" && profile?.hasDisability)
        return false;

      return true;
    });
    setFilteredSubmissions(filtered);
  }, [
    submissions,
    filterRound,
    filterProgram,
    filterEvaluated,
    filterDisability,
  ]);

  const uniqueFormNames = [...new Set(submissions.map((s) => s.form?.name))];
  const uniqueProgramNames = [
    ...new Set(submissions.map((s) => s.form?.program)),
  ];

  const columns = [
    {
      title: "Program",
      dataIndex: ["form", "program"],
      key: "program",
    },
    {
      title: "Round",
      dataIndex: ["form", "name"],
      key: "round",
    },
    {
      title: "Name",
      dataIndex: ["user", "name"],
      key: "name",
    },
    {
      title: "Email",
      dataIndex: ["user", "email"],
      key: "email",
    },
    {
      title: "Participant CV",
      dataIndex: ["participantProfile", "cvUrl"],
      key: "participant_cv",
      render: (cvUrl) => (
        <Button
          type="link"
          href={cvUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          View CV
        </Button>
      ),
    },
    {
      title: "Has Disability?",
      dataIndex: ["participantProfile", "hasDisability"],
      key: "has_disability",
      render: (d) =>
        d ? (
          <Tag icon={<IdcardOutlined />} color="red">
            Yes
          </Tag>
        ) : (
          <Tag icon={<IdcardOutlined />} color="green">
            No
          </Tag>
        ),
    },
    {
      title: "Disability Spec.",
      dataIndex: ["participantProfile", "disabilitySpecification"],
      key: "disability_spec",
      render: (txt) => txt || <Tag>—</Tag>,
    },
    {
      title: "Judge Score",
      dataIndex: ["judgeAssignment", "judgeScore"],
      key: "judgeScore",
      render: (score) => (score != null ? score : "Not evaluated"),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Link
          to={`/${record.form.program}/evaluatesubmission/${record.submissionId}`}
        >
          <Button type="primary" block>
            {record.judgeScore ? "Update Evaluation" : "Evaluate"}
          </Button>
        </Link>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Title level={3}>Submission List</Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col>
          <Select
            placeholder="Filter by Program"
            value={filterProgram}
            onChange={setFilterProgram}
            style={{ width: 200 }}
            allowClear
          >
            {uniqueProgramNames.map((programName) => (
              <Option key={programName} value={programName}>
                {programName}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Select
            placeholder="Filter by Round"
            value={filterRound}
            onChange={setFilterRound}
            style={{ width: 200 }}
            allowClear
          >
            {uniqueFormNames.map((formName) => (
              <Option key={formName} value={formName}>
                {formName}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Select
            placeholder="Evaluation Status"
            value={filterEvaluated}
            onChange={setFilterEvaluated}
            style={{ width: 200 }}
            allowClear
          >
            <Option value="evaluated">Evaluated</Option>
            <Option value="Not-evaluated">Not Evaluated</Option>
          </Select>
        </Col>
        <Col>
          <Select
            placeholder="Disability Status"
            value={filterDisability}
            onChange={setFilterDisability}
            style={{ width: 200 }}
            allowClear
          >
            <Option value="with">With Disability</Option>
            <Option value="without">Without Disability</Option>
          </Select>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={filteredSubmissions}
        rowKey="submissionId"
        bordered
        loading={loading}
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
};

export default JudgeHome;
